<template>
  <AuthGuard>
    <v-container class="sync-page pa-4 pa-md-6">
      <PageHeader
        title="Data sync"
        back-to-label="Customer profile"
        :show-back="true"
        :back-to="URLs.PAGE_CUSTOMER_EDIT(customerId ?? '')"
        compact
      />

      <Loading v-if="!sourceInfo" />
      <template v-else>
        <v-card class="sync-hero mb-6" rounded="lg" elevation="0" border>
          <v-card-text class="thor-profile-card-body">
            <div class="d-flex flex-wrap align-center justify-space-between gap-4">
              <div class="d-flex align-center min-w-0 thor-profile-avatar-name-gap">
                <ServiceLogo :service="sourceInfo.feed" size="56px" class="shrink-0" />
                <div class="min-w-0">
                  <div class="text-h6 font-weight-bold text-truncate">
                    {{ sourceInfo.name || "Source" }}
                  </div>
                  <v-chip class="mt-1" size="small" variant="tonal" color="primary">
                    {{ getFeedNameFromFeedType(sourceInfo.feed) }}
                  </v-chip>
                </div>
              </div>
              <v-btn
                color="primary"
                variant="tonal"
                prepend-icon="mdi-refresh"
                class="text-transform-none shrink-0"
                :loading="state.refreshing"
                @click="fetchAccountInfo(customerId, sourceId)"
              >
                Refresh
              </v-btn>
            </div>
          </v-card-text>
        </v-card>
        <DataSyncTable :account="sourceInfo" />
      </template>
    </v-container>
  </AuthGuard>
</template>

<script setup lang="ts">
import { useAccountStore } from "@/stores/account";
import { IApp } from "@/types";
import { getFeedNameFromFeedType, makeAlert } from "@/utils";
import { computed, onMounted, ref } from "vue";
import useRouteParams from "@/hooks/useRouteParams";
import AuthGuard from "@/components/Auth/AuthGuard.vue";
import { useAuthStore } from "@/stores/auth";
import DataSyncTable from "@/components/Widgets/DataSyncTable.vue";
import Loading from "@/components/Widgets/Loading.vue";
import PageHeader from "@/components/PageHeader.vue";
import ServiceLogo from "@/components/Widgets/ServiceLogo.vue";
import { URLs } from "@/constants";

const authStore = useAuthStore();
const sourceInfo = ref<
  IApp.IBirdEyeAccount | IApp.IGoogleSearchConsoleAccount | IApp.IGoogleAnalyticsAccount
>();
const user = computed(() => authStore.user);

const state = ref({
  refreshing: false
});

const accountStore = useAccountStore();

const { customerId, sourceId } = useRouteParams();

const fetchAccountInfo = async (customerId: string | undefined, sourceId: string | undefined) => {
  if (user.value?.id && sourceId && customerId) {
    state.value.refreshing = true;
    try {
      const response = await accountStore.fetchAccountInfo(
        user.value.id,
        customerId,
        sourceId,
        Date.now()
      );
      if (response.status) {
        sourceInfo.value = response.data.account;
      } else {
        makeAlert(response.message, "error");
      }
    } finally {
      state.value.refreshing = false;
    }
  }
};
onMounted(() => fetchAccountInfo(customerId.value, sourceId.value));
</script>

<style scoped>
.sync-page {
  max-width: 1120px;
  margin-left: auto;
  margin-right: auto;
}

.sync-hero {
  background: rgb(var(--v-theme-surface));
}

.thor-profile-card-body {
  padding: var(--thor-profile-card-padding);
}
</style>
